<template>
  <bm-page
    v-bind="$attrs"
    :subtitle="computedSubtitle"
    @content-height-resized="onAvailableContentHeightUpdated"
  >
    <template #grid-controls>
      <slot
        name="grid-controls"
        :grid-configuration="gridConfiguration"
        :grid-state="gridState"
        :show-search="showSearch"
      >
        <bm-grid-control-panel
          :grid-configuration="gridConfiguration"
          :grid-state="gridState"
          :show-search="showSearch"
          :show-expand-all="showExpandAll"
          :all-rows-expanded="allRowsExpanded"
          :custom-print-title="customPrintTitle"
          @toggle-expand-all="emit('toggle-expand-all')"
        >
          <template #additional-controls>
            <bm-btn
              v-if="mapMarkers && !showMapByDefault"
              button-type="secondary"
              @click="toggleMap()"
            >
              <bm-icon icon="Map" :left="screenSize.smAndUp" />
              {{ screenSize.smAndUp ? $m.map_view : "" }}
            </bm-btn>
          </template>
        </bm-grid-control-panel>
      </slot>
    </template>

    <v-container
      v-if="hasDataFilter"
      ref="dataFiltersRef"
      fluid
      px-0
      class="bm-grid-data-filter"
    >
      <v-row>
        <v-col>
          <slot name="grid-data-filter" />
        </v-col>
      </v-row>
    </v-container>

    <v-expand-transition>
      <v-container
        v-if="shouldShowMap && !useSideBySideMap"
        v-show="mapIsVisible"
        fluid
        pa-0
      >
        <v-row>
          <v-col>
            <slot name="map">
              <bm-map-loader
                class="gMap pb-4"
                :markers="mapMarkers"
                :value="selectedMapMarker"
                :visble="mapIsVisible"
                :update-bounds-on-marker-update="updateBoundsOnMarkerUpdate"
                :use-marker-clusterer="useMarkerClusterer"
                :polylines="polylines"
                @update:value="onUpdateSelectedMapMarker"
              />
            </slot>
          </v-col>
        </v-row>
      </v-container>
    </v-expand-transition>

    <v-container fluid pa-0>
      <v-row no-gutters>
        <v-col
          cols="12"
          :md="useSideBySideMap && shouldShowMap && mapIsVisible ? 6 : 12"
        >
          <v-container fluid pa-0>
            <v-row no-gutters>
              <v-col v-if="$slots['above-grid']" cols="12">
                <slot name="above-grid" />
              </v-col>
              <v-col cols="12">
                <slot
                  name="grid"
                  :grid-state="gridState"
                  :grid-configuration="gridConfiguration"
                  :set-filter-summary="setFilterSummary"
                  :height="gridHeight"
                  :override-height="gridHeightWithUnits"
                  :initial-visible-rows="200"
                  :allow-select="allowSelect"
                />
              </v-col>
            </v-row>
          </v-container>
        </v-col>
        <v-slide-x-transition leave-absolute>
          <v-col
            v-if="shouldShowMap && useSideBySideMap"
            v-show="mapIsVisible"
            cols="12"
            :md="mapIsVisible ? 6 : 0"
            class="pl-4"
          >
            <slot name="map">
              <bm-map-loader
                class="pb-4 gMap"
                :markers="mapMarkers"
                :value="selectedMapMarker"
                :visible="mapIsVisible"
                :update-bounds-on-marker-update="updateBoundsOnMarkerUpdate"
                :use-marker-clusterer="useMarkerClusterer"
                :polylines="polylines"
                @update:value="onUpdateSelectedMapMarker"
              />
            </slot>
          </v-col>
        </v-slide-x-transition>
      </v-row>
    </v-container>

    <slot />
  </bm-page>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmPage from "@/components/pages/BmPage.vue";
import BmGridControlPanel from "@/components/controls/grid/BmGridControlPanel.vue";
import type {
  BmGridConfiguration,
  BmGridState,
} from "@/components/controls/grid/BmGridData";
import { computed, useSlots } from "vue";
import type { MapMarker, Polyline } from "@/components/controls/map/maps";
import BmMapLoader from "@/components/controls/map/BmMapLoader.vue";
import { makeRef } from "@/components/composables/reactivityTypes";
import { screenSize } from "@/vue23switching/screenSize";
import type { Maybe } from "../utils/typeHelpers";

interface Props {
  subtitle?: string;
  gridState: BmGridState;
  gridConfiguration: BmGridConfiguration;
  showSearch?: boolean;
  mapMarkers?: MapMarker[];
  sideBySideMap?: boolean;
  updateBoundsOnMarkerUpdate?: boolean;
  showExpandAll?: boolean;
  allRowsExpanded?: boolean;
  allowSelect?: boolean;
  useMarkerClusterer?: boolean;
  polylines?: readonly Polyline[];
  customPrintTitle?: string | null;
  showMapByDefault?: boolean;
}

interface Events {
  (event: "toggle-expand-all"): void;
  (event: "marker-clicked", value: MapMarker): void;
}

const props = withDefaults(defineProps<Props>(), {
  subtitle: "",
  showSearch: true,
  sideBySideMap: false,
  updateBoundsOnMarkerUpdate: true,
  showExpandAll: false,
  allRowsExpanded: false,
  allowSelect: false,
  useMarkerClusterer: false,
  customPrintTitle: null,
  showMapByDefault: false,
});

const emit = defineEmits<Events>();

const slots = useSlots();

const filterSummary = makeRef("");
const availableContentHeight = makeRef<number | null>(null);
const mapIsVisible = makeRef(props.showMapByDefault);
const mapInitialised = makeRef(props.showMapByDefault);
const dataFiltersRef = makeRef<HTMLDivElement | null>(null);
const selectedMapMarker = makeRef<Maybe<MapMarker>>(null);

const hasDataFilter = computed(() => !!slots["grid-data-filter"]);

const computedSubtitle = computed(() => props.subtitle || filterSummary.value);

/**
 * Returns the maximum available space for a grid to sit within
 */
const gridHeight = computed(() => {
  if (!availableContentHeight.value) {
    return undefined;
  }

  const dataFiltersHeight =
    hasDataFilter.value && dataFiltersRef.value
      ? dataFiltersRef.value.clientHeight
      : 0;

  const gridPadding = 10;
  return availableContentHeight.value - dataFiltersHeight - gridPadding;
});

const gridHeightWithUnits = computed(() =>
  gridHeight.value ? gridHeight.value + "px" : undefined
);

const shouldShowMap = computed(
  () => !!props.mapMarkers && mapInitialised.value
);

const useSideBySideMap = computed(
  () => screenSize.mdAndUp && props.sideBySideMap
);

const setFilterSummary = (newFilterSummary: string) => {
  filterSummary.value = newFilterSummary;
};

const toggleMap = () => {
  mapIsVisible.value = !mapIsVisible.value;
  mapInitialised.value = true;
};

const onAvailableContentHeightUpdated = (newAvailableContentHeight: number) => {
  availableContentHeight.value = newAvailableContentHeight;
};

const onUpdateSelectedMapMarker = (marker: Maybe<MapMarker>) => {
  selectedMapMarker.value = marker;
  if (marker) {
    emit("marker-clicked", marker);
  }
};
</script>

<style lang="sass">

.gMap
  min-height: 40vh
</style>
